import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const InternetOfThings = () => {
  const phrases = [
    "Edge Telemetry Networks",
    "Multi-Protocol Routing",
    "Real-Time Stream Processing"
  ];

  const cardData = [
    { type: 'cyan-core', content: '📟' },
    { type: 'telemetry-wave', content: '📡' },
    { type: 'gateway-stack', content: '⚙️' },
    { type: 'sensor-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );
    
    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "📟", title: "Industrial Edge Firmware & OS Systems", desc: "Build low-footprint, embedded operating kernels and driver abstractions designed to run on resource-constrained microcontrollers and edge hardware configurations." },
    { icon: "📡", title: "Multi-Protocol Device Gateway Arrays", desc: "Deploy resilient connection topologies supporting real-time data ingestion via MQTT, CoAP, HTTP, OPC UA, and specialized industrial bus interfaces." },
    { icon: "⚡", title: "Sub-Millisecond Edge Compute Nodes", desc: "Process intensive machine computer vision inputs, filtering matrix calculations, and threshold evaluations directly at the physical site location." },
    { icon: "🔄", title: "Secure OTA Fleet Management Systems", desc: "Construct fault-tolerant over-the-air firmware deployment pipelines featuring micro-differential updates and automatic image rollbacks." },
    { icon: "🗄️", title: "High-Volume Time-Series Ingestion Platforms", desc: "Architect ultra-scalable data collection brokers capable of processing millions of parallel sensor state changes per second cleanly." },
    { icon: "🛡️", title: "Zero-Trust Device Identity Protection", desc: "Enforce deep silicon-to-cloud security barriers using Hardware Security Modules (HSM), X.509 device certificates, and fully encrypted communication links." },
    { icon: "📊", title: "Digital Twin State Synchronization Engines", desc: "Map physical hardware environments into cloud-hosted logical twins to maintain precise real-time situational tracking and simulation boundaries." }
  ];

  const advancedServices = [
    { icon: "🔋", title: "Ultra-Low Power Optimization Architectures", desc: "Tune sleep-wake cycles and telemetry burst metrics to extend remote battery life cycles out to multiple years on field deployments." },
    { icon: "🔗", title: "Mesh Network Field Deployments", desc: "Configure smart decentralized routing paths across thousands of edge hardware units via BLE Mesh, Zigbee, or Thread networks." },
    { icon: "🚨", title: "Predictive Thermal & Stress Diagnostics", desc: "Analyze high-frequency physical vibrations and heat fluctuations instantly to safely catch machinery structural failures before they break." },
    { icon: "🚜", title: "Geospatial Telematics & Tracking Infrastructure", desc: "Unify cellular asset location nodes, GNSS receivers, and motion telemetry grids into clear, live asset mapping dashboards." },
    { icon: "🎯", title: "Real-Time Calibration Feedback Loops", desc: "Issue rapid target adjustments from remote calculation nodes directly back to field hardware control systems safely." },
    { icon: "🏭", title: "SCADA & Factory Field Integrations", desc: "Interface modern digital platforms cleanly with old industrial machinery PLC setups without breaking existing factory hardware." },
    { icon: "🌐", title: "Long-Range Network Implementations", desc: "Design data collection links for remote, sparse environments using low-power wide-area infrastructure like LoRaWAN and NB-IoT." },
    { icon: "🧪", title: "Automated Device Stress Diagnostics", desc: "Run background memory monitoring and packet drop tests to catch software leaks before edge nodes drop off the grid." },
    { icon: "📦", title: "Cold-Chain Asset Monitoring Matrices", desc: "Maintain continuous, auditable logs tracking environmental variables, humidity levels, and impacts for highly sensitive commercial shipments." }
  ];

  const technologies = {
    "Embedded Hardware & OS": ["FreeRTOS / Zephyr OS", "Embedded Linux Kernels", "C / C++ Hardware Compilers", "ESP32 / Raspberry Pi Architectures"],
    "Protocols & Connectivity": ["MQTT / MQTT-SN Brokers", "CoAP / LwM2M Infrastructure", "OPC UA / Modbus Bridges", "LoRaWAN / NB-IoT Systems"],
    "Cloud IoT Platforms": ["AWS IoT Core Frameworks", "Azure IoT Hub Matrices", "GCP IoT Edge Connectors", "HiveMQ Broker Nodes"],
    "Stream & Data Processing": ["Apache Kafka Clusters", "TimescaleDB / InfluxDB Packs", "Apache Flink Analytical Nodes", "Node-RED Rapid Prototyping"],
    "Edge Analytics & Validation": ["TensorFlow Lite Engines", "Edge Impulse Optimization", "Grafana Monitoring Frameworks", "Wireshark Telemetry Analyzers"]
  };

  const processSteps = [
    { num: "01", title: "Hardware Architecture Scoping", desc: "Analyzing target deployment site metrics, power constraints, and setting structural telemetry targets." },
    { num: "02", title: "Sensor Firmware Engineering", desc: "Programming low-level drivers, peripheral sensor interfaces, and localized data caching scripts." },
    { num: "03", title: "Gateway & Network Routing", desc: "Configuring local edge server nodes, packet packaging layers, and secure validation handshakes." },
    { num: "04", title: "Cloud Stream Ingestion Setups", desc: "Building scalable topic routing models, high-speed brokers, and initial processing pipelines." },
    { num: "05", title: "Silicon Identity Security Lock", desc: "Provisioning hardware cryptographic key pairings and hardening encryption profiles across all field nodes." },
    { num: "06", title: "Production Fleet Provisioning", desc: "Deploying active hardware arrays out into physical environments with verified data connection validations." },
    { num: "07", title: "Continuous OTA Cycle Tuning", desc: "Monitoring battery drain metrics, applying software patches remotely, and scaling processing nodes." }
  ];

  const industries = [
    "Smart Factory Industrial Automation (IIoT)", "Connected Healthcare & Remote Patient Care", "Precision Agritech Sensor Ecosystems",
    "Intelligent Urban Power Distribution Grids", "Automated Fleet Telematics & Tracking", "Asset Traceability Logistics Platforms",
    "Smart Structural Building Optimization", "Automated Water & Environmental Quality Logging", "Connected Mining Facility Operational Management",
    "Smart Commercial Cold-Chain Shipping", "Retail Smart Inventory Sensing Arrays", "Renewable Energy Field Telemetry Arrays"
  ];

  const designBenefits = [
    "100% Granular Real-Time Field Operations Visibility", "Up to 45% Drop in Physical Machinery Breakdown Incidents", "Zero-Touch Automated OTA Software Deployments", 
    "Drastic Reductions in Manual Site Inspection Requirements", "Airtight End-to-End Cryptographic Asset Security", "Scalable Processing Architecture Handling Billions of Points", 
    "Massive Optimizations in Field Power Resource Use", "Instant Event Detection and Automated Emergency Shutdowns"
  ];

  const whyChooseUs = [
    "Certified Embedded System Architecture Specialists", "Proven Scale in High-Volume Hardware Deployments", "Airtight Zero-Trust Hardware-Level Security",
    "Deep Competency in Specialized Industrial Protocols", "Platform-Agnostic Hybrid Infrastructure Implementations", "Resilient Edge Computing Code Topologies",
    "End-to-End IoT Fleet Lifecycle Engineering Ownership", "Transparent Analytical ROI Performance Reporting", "High-Availability Data Ingestion Infrastructure SLAs",
    "Experienced Network Signal Transmission Designers", "Iterative Prototyping and Precision Testing Methods", "100% Continuous Hardware Uptime System Commitments"
  ];

  return (
    <>
    <div className="iot-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>
      
      {/* ================= HERO SECTION ================= */}
      <div 
        className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden" 
        style={{ 
          backgroundImage: "url('./images/home.jpg')",
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '85vh',
          backgroundColor: '#0b132b' // Dark carbon obsidian/teal fallback
        }}
      >
        {/* Background Grid Lines */}
        <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
          <div className="grid-line line-v-1"></div>
          <div className="grid-line line-v-2"></div>
          <div className="grid-line line-v-3"></div>
        </div>

        {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
        <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
        </div>

        <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
          <div className="marquee-content text-white">
            <span>Internet &nbsp; of &nbsp; Things &nbsp; &amp; &nbsp;</span>
            <span>Connected Architecture Platforms </span>
          </div>
        </div>

        {/* Main Content Layout */}
        <div className="container position-relative z-3 my-5">
          <div className="row align-items-center py-4">
            
            {/* Left Side Info */}
            <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
              <div className="asterisk-icon mb-3 text-cyan fs-3" style={{ color: '#06b6d4' }}>✳</div>
              <h1 className="fw-bold mb-3 display-5">Internet of Things</h1>
              <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                At Affsoft IT Solutions Pvt. Ltd., we transform physical operations into active digital networks. Our IoT engineering practice unifies secure embedded device firmware development with low-latency streaming infrastructure and smart edge data calculations to turn raw physical telemetry into high-value automated systems.
              </p>
              <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                <span>➔</span>
                <span>Propose an IoT Blueprint</span>
              </button>
            </div>

            {/* Center Section */}
            <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
              <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                <span className="cursor fs-4 animate-blink" style={{ color: '#06b6d4' }}>|</span>
              </div>
            </div>

            {/* Right Section */}
            <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
              <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                  <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ================= CORE SOLUTIONS SECTION ================= */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
            <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#06b6d4' }}>Connected Hardware Scaling</span>
            <h2 className="fw-bold text-dark display-6">Deploy High-Availability Device Networks & Intelligent Edge Grids</h2>
            <p className="text-muted mt-3">
              Unstable device connectivity, hidden cybersecurity exposures at remote field sites, and massive data volume overloads can stall standard digital infrastructure projects. At Affsoft IT Solutions Pvt. Ltd., we engineer highly stable, securely managed hardware-to-cloud telemetry structures.
            </p>
          </div>

          <div className="row g-4">
            {coreServices.map((service, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                  <div className="fs-1 mb-3" style={{ color: '#06b6d4' }}>{service.icon}</div>
                  <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                  <p className="text-muted mb-0 small">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Business Value Checklist */}
          <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
            <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Disciplined IoT Systems Engineering</h5>
            <div className="row g-3">
              {designBenefits.map((benefit, index) => (
                <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                  <span className="text-success fs-5">✓</span>
                  <span className="text-secondary small fw-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
            <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#06b6d4' }}>Distributed Telemetry Layers</span>
            <h2 className="fw-bold text-dark display-6">Adaptive Device Capabilities Built for Heavy Industrial Workloads</h2>
            <p className="text-muted mt-3">
              We upgrade basic remote hardware setups into highly resilient self-diagnostic infrastructure networks. From mapping micro-differential software updates to tracking mobile telematics, our frameworks stabilize edge workflows.
            </p>
          </div>

          <div className="row g-4">
            {advancedServices.map((service, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                  <div className="fs-1 mb-3" style={{ color: '#06b6d4' }}>{service.icon}</div>
                  <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                  <p className="text-muted mb-0 small">{service.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TECHNOLOGIES SECTION ================= */}
      <section className="py-5 bg-dark text-white">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#22d3ee' }}>Industrial IoT Core Stack</span>
            <h2 className="fw-bold display-6">IoT Infrastructure & Edge Analytics We Leverage</h2>
            <p className="text-light-50">We build, manage, and scale connected device ecosystems using trusted open hardware environments.</p>
          </div>

          <div className="row g-4 justify-content-center">
            {Object.entries(technologies).map(([category, list], index) => (
              <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                  <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#22d3ee' }}>{category}</h5>
                  <ul className="list-unstyled mb-0">
                    {list.map((tech, i) => (
                      <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                        <span className="opacity-75" style={{ color: '#22d3ee' }}>•</span> {tech}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ROADMAP PROCESS ================= */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Fleet Lifecycle</span>
            <h2 className="fw-bold text-dark display-6">Our Engineered IoT Architecture Framework</h2>
            <p className="text-muted">A clear, highly disciplined development blueprint designed to transition custom hardware setups safely from prototype testboards onto continuous grid operations.</p>
          </div>

          <div className="row g-3">
            {processSteps.map((step, index) => (
              <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#06b6d4' }}>
                  <div className="fw-bold display-6 mb-2" style={{ color: '#06b6d4' }}>{step.num}</div>
                  <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                  <p className="text-muted small mb-0">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRIES SERVED ================= */}
      <section className="py-5 bg-light">
        <div className="container text-center">
          <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Connect</h2>
          <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
            {industries.map((industry, index) => (
              <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                🏢 {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE AFFSOFT ================= */}
      <section className="py-5 bg-white border-top">
        <div className="container">
          <div className="text-center mb-5 scroll-anim-fade-in-up">
            <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
            <p className="text-muted">We combine low-level hardware design patterns with modern cloud software infrastructure to guarantee predictable system performance.</p>
          </div>

          <div className="row g-3">
            {whyChooseUs.map((reason, index) => (
              <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                  <span className="fs-4" style={{ color: '#06b6d4' }}>🛡️</span>
                  <span className="text-dark fw-semibold small">{reason}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Styles Injection */}
      <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

    </div>

    </>
  );
};

export default InternetOfThings;